import React, { useEffect } from 'react';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useOrganizationSubscription } from 'Shared/utils/hooks/use-organization-subscription';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useToggle } from 'Shared/utils/hooks';

// Components
import { WpModal, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';
import { WpText } from 'Wpds/components/text';
import { WpLink } from 'Wpds/components/link';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';

// utils
import { trackEvent } from 'Shared/tracking/tracking';

// Styles
import './CharmaLegacySunsetModal.scss';

type Props = {
  onDismiss: Function;
};
const CharmaLegacySunsetModal = ({ onDismiss }: Props) => {
  const [isClosing, toggleIsClosing] = useToggle(false);

  const { id: currentUserId, isBillingContact } = useCurrentUser();
  const { organization } = useOrganization();
  const { intercomContactSalesEventName } = useApp();
  const { isPaidPlan } = useOrganizationSubscription();

  const handleDismissClick = () => {
    toggleIsClosing();
    onDismiss();
  };

  useEffect(() => {
    trackEvent('Viewed Charma legacy sunset modal', {
      user: currentUserId,
      isBillingContact,
      isPaidPlan,
      organization: organization.id,
    });
  }, []);

  const baseClassName = 'CharmaLegacySunsetModal';
  return (
    <WpModal isOpen={true} size="md" dismissOnBackdrop={false}>
      <WpModalBody padding={0}>
        <div className={`${baseClassName}__img-wrapper`}>
          <WpIllustration
            src={Illustrations.person_with_megaphone_with_purple_bg}
            className={`${baseClassName}__img-wrapper__img`}
          />
          <div className={`${baseClassName}__img-wrapper__close__wrapper__btn`}>
            <WpButton
              variant="tertiary"
              onClick={handleDismissClick}
              qaTest="dismiss-new-charma-sunset-modal"
              loading={isClosing}
            >
              <WpIcon name="times" />
            </WpButton>
          </div>
        </div>
      </WpModalBody>
      <WpModalBody>
        <WpText color="purple-800" bold>
          ACTION REQUIRED!
        </WpText>
        <WpText bold variant="bodyXxl">
          Your account will be deactivated March 31, 2025.
        </WpText>
        <WpText>
          As part of Charma&apos;s acquisition by The Predictive Index, your current software experience is being
          sunsetted as of March 31, 2025.
        </WpText>
        <WpText>
          To continue enjoying PI Perform, including new features like the PI Behavioral Assessment, you will need to
          sign a new contract by March 31, 2025.{' '}
          {isPaidPlan && `As a special offer, you can lock in your current Charma rate for the first year.`}
        </WpText>

        {isBillingContact ? (
          <>
            <WpText>
              If you&apos;re interested in keeping your Perform access, please connect with sales to update your
              subscription.
            </WpText>
            <WpText>
              For further questions, please contact{' '}
              <WpLink href="mailto:jklein@predictiveindex.com">jklein@predictiveindex.com</WpLink>.
            </WpText>
            <WpText>Looking forward to your continued partnership!</WpText>
          </>
        ) : (
          <WpContainer color="gray">
            <WpContainerBody>
              <WpText>
                If you&apos;re interested in keeping your Perform access, please have your billing administrator (listed
                below) get in touch with our Sales team.
              </WpText>
              <WpText noMargin bold>
                {organization.billingContact.full_name}
              </WpText>

              <WpText noMargin>
                <WpLink href={`mailto:${organization.billingContact.email}`}>
                  {organization.billingContact.email}
                </WpLink>
              </WpText>
            </WpContainerBody>
          </WpContainer>
        )}
      </WpModalBody>
      {isBillingContact && (
        <WpModalFooter>
          <WpButton dataAttributes={{ 'data-intercom': intercomContactSalesEventName }}>Connect with Sales</WpButton>
        </WpModalFooter>
      )}
    </WpModal>
  );
};

export default CharmaLegacySunsetModal;
